import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';

import FormInput from '../form/input';
import FormButton from '../form/button';

const BankDetailsModal = ({
  modal,
  toggleModal,
  modalOpen,
  bankDetails,
  updateProfile,
  setProfile,
  setSession,
  setUnexpectedError
}) => {
  const { accountName, accountNumber, name, swiftCode, iBan, address } = bankDetails || {};

  const BankDetailSchema = Yup.object().shape({
    accountName: Yup.string().required('*Account Name is Required!'),
    accountNumber: Yup.string().required('*Account Number is Required!'),
    name: Yup.string().required('*Bank Name is Required!'),
    swiftCode: Yup.string().required('*Swift code is Required!'),
    // iBan: Yup.string().required('*IBAN is Required!'),
    street: Yup.string(),
    state: Yup.string().required('*State is Required!'),
    country: Yup.string().required('*Country is Required!')
  });

  const INITIAL_STATE = {
    accountName: accountName || '',
    accountNumber: accountNumber || '',
    name: name || '',
    swiftCode: swiftCode || '',
    iBan: iBan || '',
    street: (address && address.street) || '',
    state: (address && address.state) || '',
    country: (address && address.country) || ''
  };

  return (
    <div className={modal ? 'show' : 'hidden'}>
      <div className="modal-bg flex items-center justify-center fixed z-50 top-0 left-0 right-0 bottom-0 w-screen h-screen">
        <div className="">
          <div className="relative w-85 md:w-125 py-10 mx-auto bg-white rounded px-4 shadow">
            <div className="my-6">
              <h4 className="font-bold text-xl">Update your Bank Details</h4>
              <p>Make changes to your basic info here.</p>
            </div>

            <button
              className="absolute right-0 top-0 pr-6 pt-6 text-green-600 focus:outline-none"
              onClick={toggleModal}
            >
              <FaTimes size="20px" />
            </button>
            <Formik
              initialValues={INITIAL_STATE}
              validationSchema={BankDetailSchema}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                let bank = {
                  accountName: values.accountName,
                  accountNumber: values.accountNumber,
                  name: values.name,
                  swiftCode: values.swiftCode,
                  iBan: values.iBan,
                  address: {
                    street: values.street,
                    state: values.state,
                    country: values.country
                  }
                };
                (async () => {
                  try {
                    setSubmitting(true);
                    const response = await updateProfile({ bank });
                    setSubmitting(false);
                    if (response.code === 200) {
                      setProfile(response.data);
                      toast.success(response.message, {
                        position: toast.POSITION.TOP_RIGHT
                      });
                      toggleModal();
                    } else {
                      toast.error(response.data.message, {
                        position: toast.POSITION.TOP_RIGHT
                      });
                    }
                  } catch (error) {
                    if (error && error.status === 401) setSession(false);
                    else setUnexpectedError('Unexpected Server Error!');
                  }
                })();
              }}
            >
              {({
                values,
                errors,
                dirty,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting
              }) => (
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-gap-4"
                >
                  <FormInput
                    error={errors.accountName}
                    touched={touched.accountName}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.accountName}
                    type="text"
                    id="accountName"
                    placeholder="Account Name"
                    inputType
                  />

                  <FormInput
                    error={errors.accountNumber}
                    touched={touched.accountNumber}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.accountNumber}
                    type="text"
                    id="accountNumber"
                    placeholder="Account Number"
                    inputType
                  />

                  <FormInput
                    error={errors.name}
                    touched={touched.name}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.name}
                    type="text"
                    id="name"
                    placeholder="Bank Name"
                    inputType
                  />

                  <FormInput
                    error={errors.swiftCode}
                    touched={touched.swiftCode}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.swiftCode}
                    type="text"
                    id="swiftCode"
                    placeholder="Swift Code"
                    inputType
                  />

                  <FormInput
                    error={errors.iBan}
                    touched={touched.iBan}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.iBan}
                    type="text"
                    id="iBan"
                    placeholder="IBAN"
                    inputType
                  />

                  <FormInput
                    error={errors.street}
                    touched={touched.street}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.street}
                    type="text"
                    id="street"
                    placeholder="Street"
                    inputType
                  />

                  <FormInput
                    error={errors.state}
                    touched={touched.state}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.state}
                    type="text"
                    id="state"
                    placeholder="State/Region/City"
                    inputType
                  />

                  <FormInput
                    error={errors.country}
                    touched={touched.country}
                    className="w-full"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.country}
                    type="text"
                    id="country"
                    placeholder="Country"
                    inputType
                  />

                  <div className="hidden md:block" />

                  <div className="flex items-center w-full mt-4">
                    <FormButton
                      loading={isSubmitting}
                      disabled={!dirty}
                      color="bg-cf-300"
                      name="Save"
                      className="w-full"
                      btnPad={'px-5 py-4 mr-2 text-base font-bold'}
                    />
                    <button
                      type="button"
                      onClick={toggleModal}
                      className="rounded hover:shadow-lg py-4 px-4 text-base font-bold text-cf-300 bg-cf-200 focus:outline-none md:w-64"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankDetailsModal;
